import { useEffect } from 'react';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/16x16/hex.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/16x16/lightdash.svg';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/16x16/looker.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/16x16/power-bi.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/16x16/sigma.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/16x16/tableau.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/16x16/thoughtspot.svg';
import { FlexColumn } from '@elementary/common-ui/components';
import { Collapse, ToggleButtonGroup } from '@mui/material';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
  BiToolForm,
  FetchBiIntegrationApi,
  SigmaForm as SigmaFormFields,
  TableauForm as TableauFormFields,
  LookerForm as LookerFormFields,
  PowerBiForm as PowerBiFormFields,
  ThoughtSpotForm as ThoughtSpotFormFields,
  HexForm as HexFormFields,
  LightdashForm as LightdashFormFields,
  LookerSettingsApi,
  TableauSettingsApi,
  PowerBiSettingsApi,
  SigmaSettingsApi,
  ThoughtSpotSettingsApi,
  HexSettingsApi,
  LightdashSettingsApi,
} from 'shared/types';
import { IntegrationError } from '../../../../types';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import DialogToggleButton from '../../../shared/DialogToggleButton';
import HexDialogForm from './HexDialogForm';
import LightdashDialogForm from './LightdashDialogForm';
import LookerForm from './LookerDialogForm';
import PowerBiForm from './PowerBiDialogForm';
import SigmaDialogForm from './SigmaDialogForm';
import TableauForm from './TableauDialogForm';
import ThoughtSpotForm from './ThoughtSpotDialogForm';

const getDefaultValues = (
  selectedIntegration: FetchBiIntegrationApi,
): BiToolForm => {
  const {
    id,
    name,
    bi_type: selectedBiTool,
    bi_settings: biSettings,
  } = selectedIntegration;

  if (selectedBiTool === 'Looker') {
    const lookerSettings = biSettings as LookerSettingsApi;
    return {
      id,
      selectedBiTool,
      lookerConnectionName: name,
      lookerServerUrl: lookerSettings?.server_url,
      lookerClientId: lookerSettings?.client_id,
      lookerClientSecret: lookerSettings?.client_secret,
      lookerGitProvider: lookerSettings?.git_provider,
      lookerGitToken: lookerSettings?.git_token,
      lookerRepoName: lookerSettings?.repo_name,
    } as LookerFormFields;
  }
  if (selectedBiTool === 'Tableau') {
    const tableauSettings = biSettings as TableauSettingsApi;
    return {
      id,
      selectedBiTool,
      tableauConnectionName: name,
      tableauPersonalAccessTokenName:
        tableauSettings?.personal_access_token_name,
      tableauPersonalAccessTokenValue:
        tableauSettings?.personal_access_token_value,
      tableauServerUrl: tableauSettings?.server_url,
      tableauSiteName: tableauSettings?.site_name,
    } as TableauFormFields;
  }
  if (selectedBiTool === 'PowerBI') {
    const powerBiSettings = biSettings as PowerBiSettingsApi;
    return {
      id,
      selectedBiTool,
      powerBiConnectionName: name,
      powerBiTenant: powerBiSettings?.tenant,
      powerBiClientId: powerBiSettings?.client_id,
      powerBiClientSecret: powerBiSettings?.client_secret,
      powerBiWorkspaces: powerBiSettings?.workspaces,
    } as PowerBiFormFields;
  }
  if (selectedBiTool === 'Sigma') {
    const sigmaSettings = biSettings as SigmaSettingsApi;
    return {
      id,
      selectedBiTool,
      sigmaConnectionName: name,
      sigmaCloudProvider: sigmaSettings?.cloud_provider,
      sigmaClientId: sigmaSettings?.client_id,
      sigmaClientSecret: sigmaSettings?.client_secret,
    } as SigmaFormFields;
  }
  if (selectedBiTool === 'ThoughtSpot') {
    const thoughtSpotSettings = biSettings as ThoughtSpotSettingsApi;
    return {
      id,
      selectedBiTool,
      thoughtSpotConnectionName: name,
      thoughtSpotUsername: thoughtSpotSettings?.username,
      thoughtSpotSecretKey: thoughtSpotSettings?.secret_key,
      thoughtSpotBaseUrl: thoughtSpotSettings?.base_url,
    } as ThoughtSpotFormFields;
  }
  if (selectedBiTool === 'Hex') {
    const hexSettings = biSettings as HexSettingsApi;
    return {
      id,
      selectedBiTool,
      hexConnectionName: name,
      hexWorkspaceToken: hexSettings?.workspace_token,
      hexCustomUrl: hexSettings?.custom_url || '',
    } as HexFormFields;
  }
  if (selectedBiTool === 'Lightdash') {
    const lightdashSettings = biSettings as LightdashSettingsApi;
    return {
      id,
      selectedBiTool,
      lightdashConnectionName: name,
      lightdashHostUrl: lightdashSettings?.host_url || '',
      lightdashApiToken: lightdashSettings?.api_token,
      lightdashProjectUuids: lightdashSettings?.project_uuids || [],
    } as LightdashFormFields;
  }
  return {} as BiToolForm;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: BiToolForm) => void;
  error?: IntegrationError;
  loading?: boolean;
  selectedIntegration?: FetchBiIntegrationApi;
  mode?: 'update' | 'create';
};

const BiToolDialog = ({
  open,
  onClose,
  onSubmit,
  error,
  loading,
  selectedIntegration,
  mode,
}: Props) => {
  const defaultValues = selectedIntegration
    ? getDefaultValues(selectedIntegration)
    : {};

  const formMethods = useForm<BiToolForm>({
    defaultValues,
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
    watch,
  } = formMethods;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect your BI tool"
      description="Give Elementary access to your BI tool for impact analysis."
      onSubmit={handleSubmit(onSubmit)}
      docsLink="https://docs.elementary-data.com/cloud/integrations/bi/connect-bi-tool"
      isValid={
        isValid &&
        (watch('selectedBiTool') === 'Looker'
          ? !!watch('lookerGitProvider')
          : true)
      }
      error={error}
      loading={loading}
      reset={() => reset(defaultValues)}
    >
      <ToggleButtonGroup
        fullWidth
        exclusive
        value={watch('selectedBiTool')}
        onChange={(_, val) => {
          setValue('selectedBiTool', val);
        }}
      >
        <DialogToggleButton
          value="Tableau"
          disabled={mode === 'update' && watch('selectedBiTool') !== 'Tableau'}
        >
          <TableauIcon />
          Tableau
        </DialogToggleButton>
        <DialogToggleButton
          value="Looker"
          disabled={mode === 'update' && watch('selectedBiTool') !== 'Looker'}
        >
          <LookerIcon />
          Looker
        </DialogToggleButton>
        <DialogToggleButton
          value="PowerBI"
          disabled={mode === 'update' && watch('selectedBiTool') !== 'PowerBI'}
        >
          <PowerBiIcon />
          PowerBI
        </DialogToggleButton>
        <DialogToggleButton
          value="Sigma"
          disabled={mode === 'update' && watch('selectedBiTool') !== 'Sigma'}
        >
          <SigmaIcon />
          Sigma
        </DialogToggleButton>
        <DialogToggleButton
          value="ThoughtSpot"
          disabled={
            mode === 'update' && watch('selectedBiTool') !== 'ThoughtSpot'
          }
        >
          <ThoughtSpotIcon />
          ThoughtSpot
        </DialogToggleButton>
        <DialogToggleButton
          value="Hex"
          disabled={mode === 'update' && watch('selectedBiTool') !== 'Hex'}
        >
          <HexIcon />
          Hex
        </DialogToggleButton>
        <DialogToggleButton
          value="Lightdash"
          disabled={
            mode === 'update' && watch('selectedBiTool') !== 'Lightdash'
          }
        >
          <LightdashIcon />
          Lightdash
        </DialogToggleButton>
      </ToggleButtonGroup>

      <Collapse in={!!watch('selectedBiTool')}>
        <FlexColumn sx={{ gap: 3 }}>
          {watch('selectedBiTool') === 'Tableau' && (
            <TableauForm
              formMethods={formMethods as UseFormReturn<TableauFormFields>}
            />
          )}
          {watch('selectedBiTool') === 'Looker' && (
            <LookerForm
              formMethods={formMethods as UseFormReturn<LookerFormFields>}
              selectedGitProvider={watch('lookerGitProvider')}
              onGitProviderChange={(val) => setValue('lookerGitProvider', val)}
            />
          )}
          {watch('selectedBiTool') === 'PowerBI' && (
            <PowerBiForm
              formMethods={formMethods as UseFormReturn<PowerBiFormFields>}
            />
          )}
          {watch('selectedBiTool') === 'Sigma' && (
            <SigmaDialogForm
              formMethods={formMethods as UseFormReturn<SigmaFormFields>}
            />
          )}
          {watch('selectedBiTool') === 'ThoughtSpot' && (
            <ThoughtSpotForm
              formMethods={formMethods as UseFormReturn<ThoughtSpotFormFields>}
            />
          )}
          {watch('selectedBiTool') === 'Hex' && (
            <HexDialogForm
              formMethods={formMethods as UseFormReturn<HexFormFields>}
            />
          )}
          {watch('selectedBiTool') === 'Lightdash' && (
            <LightdashDialogForm
              formMethods={formMethods as UseFormReturn<LightdashFormFields>}
            />
          )}
        </FlexColumn>
      </Collapse>
    </IntegrationDialog>
  );
};

export default BiToolDialog;
